import { Band, BandStack } from '@/components/ui/Band'
import { PageHero } from '@/components/ui/PageHero'
import { ContactForm } from '@/components/ContactForm'
import { ArrowLink } from '@/components/ui/ArrowLink'
import { ExportKindsGrid } from '@/components/ui/Illustrations'
import { useDocumentMeta } from '@/lib/seo'
import { ROUTES } from '@/lib/site'

const TOTAL_LAYERS = 2

const USEFUL_FIRST_MESSAGE = [
  'Which system the export comes from — Tally, Zoho, Busy, a bank portal, a CRM.',
  'How it is shaped: title block, merged cells, subtotals, multiple tables per sheet.',
  'The notation: bracketed negatives, lakh or crore grouping, a scale note in prose.',
  'Your fiscal calendar and how many periods of history exist.',
  'Which sections you believe you hold data for beyond Finance.',
]

export default function Contact() {
  useDocumentMeta(ROUTES.contact)

  return (
    <BandStack>
      <Band layer={1} total={TOTAL_LAYERS} tone="ice" routeTab="contact">
        <PageHero
          crumb="Contact"
          title="Tell us what your exports look like."
          lede="The most useful first message is a description of the files you already have — what system produced them, what shape they are in, which sections they might cover. Anonymised samples are welcome; values are not needed."
        />
        <ExportKindsGrid />
      </Band>

      <Band layer={2} total={TOTAL_LAYERS} tone="warm" labelledBy="contact-form-heading">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:gap-16">
          <div className="card-spot rounded-card border border-line bg-white/85 backdrop-blur p-6 shadow-card sm:p-8">
            <h2 id="contact-form-heading" className="text-[20px] font-extrabold tracking-[-0.02em]">
              Write to us
            </h2>
            <p className="mt-3 max-w-measure text-[14px] leading-[1.7] text-muted">
              Every field except company is required. Nothing is stored in your browser.
            </p>
            <div className="mt-7">
              <ContactForm />
            </div>
          </div>

          <div className="grid gap-4 self-start">
            <div className="card-spot rounded-card border border-line bg-white/85 backdrop-blur p-6 shadow-card">
              <h2 className="text-[16px] font-extrabold tracking-[-0.015em]">
                What makes a first message useful
              </h2>
              <ul className="mt-4 grid gap-2.5">
                {USEFUL_FIRST_MESSAGE.map((item) => (
                  <li key={item} className="flex gap-2.5 text-[13.5px] leading-[1.7] text-muted">
                    <span
                      aria-hidden="true"
                      className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-primary"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="card-spot rounded-card border border-line bg-white/85 backdrop-blur p-6 shadow-card">
              <h2 className="text-[16px] font-extrabold tracking-[-0.015em]">
                What we will not do with it
              </h2>
              <p className="mt-3 text-[13.5px] leading-[1.7] text-muted">
                We will not publish your figures, name you as a customer, or use your export as a
                testimonial. Nothing about you appears on this site unless you ask for it in
                writing.
              </p>
              <p className="mt-3 text-[13.5px] leading-[1.7] text-muted">
                If an anonymised sample is what you are comfortable sending, that is genuinely
                enough to be useful.
              </p>
            </div>

            <div className="card-spot rounded-card border border-line bg-white/85 backdrop-blur p-6 shadow-card">
              <h2 className="text-[16px] font-extrabold tracking-[-0.015em]">Before you write</h2>
              <p className="mt-3 text-[13.5px] leading-[1.7] text-muted">
                The product is not usable yet. Phase 0 is complete; the dashboard, wizard,
                projections, Ask bar and connectors are not built.
              </p>
              <p className="mt-4">
                <ArrowLink to={ROUTES.security.path}>
                  What is enforced, and what is not yet proven
                </ArrowLink>
              </p>
            </div>
          </div>
        </div>
      </Band>
    </BandStack>
  )
}
